import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import './BlogArticleBase.css';
import Navbar from '../Page/Navbar';
/**
 * Base component for blog articles
 * @param {Object} props
 * @param {string} props.title - Article title
 * @param {string} props.description - SEO description 
 * @param {string} props.slug - URL slug for the article
 * @param {string} props.image - Featured image URL for OpenGraph/Twitter
 * @param {string} props.publishDate - Publication date in ISO format
 * @param {string} props.modifiedDate - Last modified date in ISO format
 * @param {string} props.author - Article author name
 * @param {string} props.category - Article category
 * @param {Object[]} props.navItems - Items for horizontal navigation
 * @param {string} props.navTitle - Navigation section title
 * @param {Object[]} props.faqItems - FAQ items for FAQPage schema
 * @param {string[]} props.keywords - Keywords for article
 * @param {Object[]} props.breadcrumbs - Breadcrumb items
 * @param {boolean} props.showTableOfContents - Whether to show table of contents
 * @param {React.ReactNode} props.children - Article content
 */
const BlogArticleBase = ({
  title,
  description,
  slug = "",
  image = "",
  publishDate = new Date().toISOString(),
  modifiedDate = new Date().toISOString(),
  author = "AstraLight",
  category = "Astrologie",
  navItems = [],
  navTitle = "",
  faqItems = [],
  keywords = [],
  breadcrumbs = [],
  showTableOfContents = true,
  children
}) => {
  const [headings, setHeadings] = useState([]);
  const baseUrl = "https://astralight.xyz"; // À remplacer par l'URL réelle du site
  const articleUrl = `${baseUrl}/blog/${slug}`;

  // Génère le schéma JSON-LD pour l'article
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": title,
    "description": description,
    "image": image ? image : null,
    "author": {
      "@type": "Person",
      "name": author
    },
    "publisher": {
      "@type": "Organization",
      "name": "AstraLight",
      "logo": {
        "@type": "ImageObject",
        "url": `${baseUrl}/logo.png`
      }
    },
    "datePublished": publishDate,
    "dateModified": modifiedDate,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": articleUrl
    }
  };

  // Génère le schéma JSON-LD pour la FAQ si des éléments FAQ sont fournis
  const faqSchema = faqItems.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqItems.map(item => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  } : null;

  // Génère le schéma JSON-LD pour les fils d'Ariane
  const breadcrumbSchema = breadcrumbs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((crumb, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": crumb.name,
      "item": `${baseUrl}${crumb.path}`
    }))
  } : null;

  // Extrait les titres pour la table des matières
  useEffect(() => {
    if (showTableOfContents) {
      const articleElement = document.querySelector('.blog-article-content');
      if (articleElement) {
        const headingElements = articleElement.querySelectorAll('h2, h3');
        const extractedHeadings = Array.from(headingElements)
          .filter(heading => {
            // Exclure les titres qui sont dans un élément avec la classe 'exclude-from-toc'
            return !heading.closest('.exclude-from-toc');
          })
          .map((heading, index) => {
            // Ajoute un id à chaque titre s'il n'en a pas déjà un
            if (!heading.id) {
              const id = `heading-${index}`;
              heading.id = id;
            }
            return {
              id: heading.id,
              text: heading.textContent,
              level: parseInt(heading.tagName.substring(1), 10)
            };
          });
        setHeadings(extractedHeadings);
      }
    }
  }, [showTableOfContents, children]);

  return (
    <>
      <Navbar />
      <div className="blog-article-container">
        {/* SEO Optimization */}
        <Helmet>
          <title>{title}</title>
          <meta name="description" content={description} />

          {/* Canonical URL */}
          <link rel="canonical" href={articleUrl} />

          {/* Open Graph tags */}
          <meta property="og:title" content={title} />
          <meta property="og:description" content={description} />
          <meta property="og:url" content={articleUrl} />
          <meta property="og:type" content="article" />
          {image && <meta property="og:image" content={image} />}
          <meta property="og:site_name" content="AstraLight" />

          {/* Twitter Card */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={title} />
          <meta name="twitter:description" content={description} />
          {image && <meta name="twitter:image" content={image} />}

          {/* JSON-LD structured data */}
          <script type="application/ld+json">
            {JSON.stringify(articleSchema)}
          </script>

          {faqSchema && (
            <script type="application/ld+json">
              {JSON.stringify(faqSchema)}
            </script>
          )}

          {breadcrumbSchema && (
            <script type="application/ld+json">
              {JSON.stringify(breadcrumbSchema)}
            </script>
          )}
        </Helmet>

        {/* Main Content */}
        <div className="blog-content">
          {/* Horizontal Navigation Grid */}
          {navItems.length > 0 && (
            <section className="blog-nav-section">
              {navTitle && <h2 className="blog-nav-title">{navTitle}</h2>}
              <div className="blog-nav-grid">
                {navItems.map((item, index) => (
                  <a
                    href={item.url}
                    key={index}
                    className="blog-nav-item"
                    aria-label={item.title}
                  >
                    <div className="blog-nav-image-container">
                      <img
                        src={item.image}
                        alt={item.title}
                        loading="lazy"
                      />
                    </div>
                    <p className="blog-nav-item-title">{item.title}</p>
                  </a>
                ))}
              </div>
            </section>
          )}

          {/* Article Content */}
          <article className="panel">
            <h1 className="blog-article-title">{title}</h1>

            {/* Table of Contents */}
            {showTableOfContents && headings.length > 0 && (
              <nav className="table-of-contents">
                <h2>Sommaire</h2>
                <ul>
                  {headings.map((heading) => (
                    <li
                      key={heading.id}
                      className={`toc-level-${heading.level}`}
                      style={{ marginLeft: `${(heading.level - 2) * 20}px` }}
                    >
                      <a href={`#${heading.id}`}>{heading.text}</a>
                    </li>
                  ))}
                </ul>
              </nav>
            )}

            <div className="blog-article-content">
              {children}
            </div>
          </article>
        </div>
      </div>
    </>
  );
};

export default BlogArticleBase; 